/** @file Accordions used in Payment processor/gateway section */
import React, { SyntheticEvent } from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import BaseAccordion from './Accordion';
import { PaymentCredentialData, PaymentGatewayType } from '../../../store/modules/merchant/types';
import { testId as parentTestId } from '../util';
import { pspInputForms } from '../../../store/modules/merchant/constants';

export const testId = `${parentTestId}-PAYMENT_GATEWAY`;

// Accordions /////////////////////////////////////////////////////////////////

type GenericAccordionProps = {
  'data-testid': string;
  isExpanded: boolean;
  onExpandToggle: (event: SyntheticEvent<Element, Event>, expanded: boolean) => any;
  title: string;
  isSelected: boolean;
  onSelectToggle: () => any;
  children: React.ReactNode;
};

/** Styled Base accordion */
const Accordion = ({
  isExpanded,
  onExpandToggle,
  isSelected,
  onSelectToggle,
  title,
  children,
  ...other
}: GenericAccordionProps) => (
  <BaseAccordion
    isExpanded={isExpanded}
    isSwitchChecked={isSelected}
    onExpandToggle={onExpandToggle}
    onSwitchToggle={onSelectToggle}
    title={title}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...other}
  >
    <Typography variant="small" sx={{ mb: 1 }}>
      Please provide the following required information for your gateway
    </Typography>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.light',
        borderRadius: '4px',
        p: 2,
      }}
    >
      {children}
    </Box>
  </BaseAccordion>
);

export const isCredentialFilled = (
  gateway: PaymentGatewayType,
  data: PaymentCredentialData,
  isPaymentPlatformEnabled = false,
): boolean => {
  const { fields = [] } = pspInputForms[gateway] ?? {};

  return fields.every(({ name }) => {
    const value = data?.[name];

    // Special logic for Stripe's api_key
    if (gateway === 'Stripe' && name === 'api_key') {
      return isPaymentPlatformEnabled ? !!value?.length : true;
    }

    return !!value?.length;
  });
};

type GatewayAccordionProps = {
  gateway: PaymentGatewayType;
  value: PaymentCredentialData | undefined;
  onChange: (value: PaymentCredentialData) => void;
  isExpanded: boolean;
  onExpandToggle: (event: SyntheticEvent<Element, Event>, expanded: boolean) => any;
  isSelected: boolean;
  onSelectToggle: () => any;
  disabled?: boolean;
  isPaymentPlatformEnabled?: boolean;
};

export const GatewayAccordion = ({
  gateway,
  value,
  onChange,
  isExpanded,
  onExpandToggle,
  isSelected,
  onSelectToggle,
  disabled,
  isPaymentPlatformEnabled = false,
}: GatewayAccordionProps) => {
  const accordion = pspInputForms[gateway];
  const defaultValues = accordion.fields.reduce(
    (acc, { name }) => ({ ...acc, [name]: '' }),
    {} as PaymentCredentialData,
  );

  return (
    <Accordion
      data-testid={`${testId}-${gateway}`}
      title={accordion.title}
      isExpanded={isExpanded}
      isSelected={isSelected}
      onExpandToggle={onExpandToggle}
      onSelectToggle={onSelectToggle}
    >
      <Stack spacing={4} sx={{ py: 1 }}>
        {accordion.fields.map((field) => {
          // Determine if field should be disabled based on current data
          let isFieldDisabled = field.disabled;

          // Special logic for Stripe api_key field - enable if IS_PAYMENT_PLATFORM is true
          if (gateway === 'Stripe' && field.name === 'api_key') {
            isFieldDisabled = !isPaymentPlatformEnabled;
          }

          return (
            <TextField
              key={field.name}
              disabled={!isSelected || disabled || isFieldDisabled}
              fullWidth={false}
              label={field.label}
              onChange={(e) =>
                onChange({
                  ...defaultValues,
                  ...value,
                  [field.name]: e.target.value,
                })
              }
              sx={{
                backgroundColor: 'white',
                '& .MuiInputLabel-root': {
                  color: 'grey.900',
                },
                '& .MuiInputLabel-root.Mui-disabled': {
                  color: 'grey.500',
                },
              }}
              value={value?.[field.name] ?? ''}
              variant="outlined"
              name={`${gateway}-${field.name}`}
            />
          );
        })}
      </Stack>
    </Accordion>
  );
};
